import React from "react";
import { Canvas, useFrame, useLoader, useThree } from "@react-three/fiber";
import { Environment } from "@react-three/drei";
import { Suspense, forwardRef, useEffect, useImperativeHandle, useMemo, useRef, useState, useCallback } from "react";
import * as THREE from "three";
import { GLTFLoader } from "three/examples/jsm/loaders/GLTFLoader";
import { OrbitControls as OrbitControlsImpl } from "three/examples/jsm/controls/OrbitControls";
import { MeshoptDecoder } from "meshoptimizer";
import { useNavigate } from "../../lib/router-compat";
import { Sun, Moon, ChevronDown, ChevronRight } from "lucide-react";
import { useLightingPreset } from "../../hooks/useLightingPreset";
import { CameraControlsButtons } from "./CameraControls";

function supportsWebGL() {
  if (typeof window === "undefined" || typeof document === "undefined") return false;
  const canvas = document.createElement("canvas");
  return Boolean(canvas.getContext("webgl2") || canvas.getContext("webgl") || canvas.getContext("experimental-webgl"));
}

class WebGLFallbackBoundary extends React.Component<
  { fallback: React.ReactNode; children: React.ReactNode },
  { hasError: boolean }
> {
  state = { hasError: false };

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  render() {
    if (this.state.hasError) return this.props.fallback;
    return this.props.children;
  }
}

// Register meshopt decoder on the GLTFLoader class so all instances support it
(GLTFLoader.prototype as any)._meshoptRegistered = false;
const _origParse = GLTFLoader.prototype.parse;
GLTFLoader.prototype.parse = function (data: any, path: string, onLoad: any, onError: any) {
  if (!(this as any)._meshoptRegistered) {
    (this as any).setMeshoptDecoder(MeshoptDecoder);
    (this as any)._meshoptRegistered = true;
  }
  return _origParse.call(this, data, path, onLoad, onError);
};

const FINISH_PRESETS: Record<string, { color: string; metalness: number; roughness: number }> = {
  "chrome": { color: "#c8c8c8", metalness: 1.0, roughness: 0.08 },
  "hi-chrome": { color: "#e8e8e8", metalness: 1.0, roughness: 0.02 },
  "hi-gloss-black": { color: "#0a0a0a", metalness: 0.6, roughness: 0.1 },
  "matte-black": { color: "#1a1a1a", metalness: 0.3, roughness: 0.7 },
  "brushed-chrome": { color: "#a0a0a0", metalness: 0.75, roughness: 0.25 },
  "gold-plated": { color: "#d4a843", metalness: 1.0, roughness: 0.03 },
  "powder-blue": { color: "#2a4a7a", metalness: 0.15, roughness: 0.65 },
  "powder-copper": { color: "#b87333", metalness: 0.2, roughness: 0.55 },
  "powder-red": { color: "#8a2a2a", metalness: 0.15, roughness: 0.65 },
  "powder-green": { color: "#2a5a3a", metalness: 0.15, roughness: 0.65 },
  "powder-white": { color: "#e8e4dc", metalness: 0.1, roughness: 0.7 },
};

// Finish options per pan type
const BASS_TOP_FINISHES = [
  { id: "hi-gloss-black", label: "Hi-Gloss Black", color: "#0a0a0a" },
  { id: "matte-black", label: "Matte Black", color: "#1a1a1a" },
  { id: "chrome", label: "Chrome", color: "#c8c8c8" },
  { id: "powder-white", label: "Painted White", color: "#e8e4dc" },
];
const BASS_BARREL_FINISHES = [
  { id: "hi-gloss-black", label: "Hi-Gloss Black", color: "#0a0a0a" },
  { id: "matte-black", label: "Matte Black", color: "#1a1a1a" },
  { id: "powder-blue", label: "Bengal Blue", color: "#2a4a7a" },
  { id: "powder-copper", label: "Candy Copper", color: "#b87333" },
  { id: "powder-red", label: "Red", color: "#8a2a2a" },
  { id: "powder-green", label: "Green", color: "#2a5a3a" },
];
const TENOR_FINISHES = [
  { id: "hi-chrome", label: "Hi-Gloss Chrome", color: "#e8e8e8" },
  { id: "chrome", label: "Chrome", color: "#c8c8c8" },
  { id: "gold-plated", label: "Gold Plated", color: "#d4a843" },
  { id: "hi-gloss-black", label: "Hi-Gloss Black", color: "#1a1a1a" },
];
const CELLO_FINISHES = [
  { id: "hi-chrome", label: "Hi-Gloss Chrome", color: "#e8e8e8" },
  { id: "chrome", label: "Chrome", color: "#c8c8c8" },
  { id: "brushed-chrome", label: "Brushed Chrome", color: "#a0a0a0" },
  { id: "hi-gloss-black", label: "Hi-Gloss Black", color: "#1a1a1a" },
];

function SwatchRow({ options, selected, onSelect }: {
  options: { id: string; label: string; color: string }[];
  selected: string;
  onSelect: (id: string) => void;
}) {
  return (
    <div className="flex items-center gap-1">
      {options.map((f) => (
        <button
          key={f.id}
          onClick={() => onSelect(f.id)}
          title={f.label}
          className={`w-5 h-5 rounded-full transition-all duration-150 ${
            selected === f.id ? "ring-1.5 ring-white/50 ring-offset-1 ring-offset-black scale-110" : "hover:scale-105"
          }`}
        >
          <div className="w-full h-full rounded-full border border-white/20" style={{ background: f.color }} />
        </button>
      ))}
    </div>
  );
}

function FinishPanel({ title, children, defaultOpen = false }: { title: string; children: React.ReactNode; defaultOpen?: boolean }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div>
      <button onClick={() => setOpen(!open)} className="flex items-center gap-1.5 w-full text-left">
        {open ? <ChevronDown size={10} className="text-white/40" /> : <ChevronRight size={10} className="text-white/40" />}
        <span className="text-[10px] tracking-[0.2em] uppercase text-white/60">{title}</span>
      </button>
      {open && <div className="mt-2 ml-4 flex flex-col gap-2">{children}</div>}
    </div>
  );
}

function Model({ url, position, rotation, finish, dualFinish, standColor }: {
  url: string;
  position: [number, number, number];
  rotation?: [number, number, number];
  finish?: string;
  dualFinish?: { top: string; barrel: string };
  standColor?: string;
}) {
  const gltf = useLoader(GLTFLoader, url);
  const clonedScene = useMemo(() => {
    const clone = gltf.scene.clone(true);
    const box = new THREE.Box3().setFromObject(clone);
    const center = box.getCenter(new THREE.Vector3());
    clone.position.sub(center);
    clone.updateMatrixWorld(true);
    return clone;
  }, [gltf]);

  useEffect(() => {
    clonedScene.updateMatrixWorld(true);
    if (dualFinish) {
      const topPreset = FINISH_PRESETS[dualFinish.top];
      const barrelPreset = FINISH_PRESETS[dualFinish.barrel];
      if (!topPreset || !barrelPreset) return;

      const bbox = new THREE.Box3().setFromObject(clonedScene);
      const upperY = bbox.min.y + (bbox.max.y - bbox.min.y) * 0.5;

      clonedScene.traverse((child) => {
        if (child instanceof THREE.Mesh && child.geometry) {
          const geo = child.geometry;
          const pos = geo.attributes.position;
          const nor = geo.attributes.normal;
          if (!pos) return;

          const colors = new Float32Array(pos.count * 3);
          const topColor = new THREE.Color(topPreset.color);
          const barrelColor = new THREE.Color(barrelPreset.color);

          const worldMatrix = child.matrixWorld;
          const normalMatrix = new THREE.Matrix3().getNormalMatrix(worldMatrix);
          const tempVec = new THREE.Vector3();
          const tempNor = new THREE.Vector3();

          for (let i = 0; i < pos.count; i++) {
            tempVec.set(pos.getX(i), pos.getY(i), pos.getZ(i));
            tempVec.applyMatrix4(worldMatrix);

            let isTop = false;
            if (nor) {
              tempNor.set(nor.getX(i), nor.getY(i), nor.getZ(i));
              tempNor.applyMatrix3(normalMatrix).normalize();
              isTop = tempNor.y > 0.3 && tempVec.y > upperY;
            } else {
              isTop = tempVec.y > upperY;
            }

            const c = isTop ? topColor : barrelColor;
            colors[i * 3] = c.r;
            colors[i * 3 + 1] = c.g;
            colors[i * 3 + 2] = c.b;
          }

          geo.setAttribute("color", new THREE.BufferAttribute(colors, 3));
          const mat = child.material as THREE.MeshStandardMaterial;
          if (mat.isMeshStandardMaterial) {
            mat.vertexColors = true;
            mat.map = null;
            mat.metalnessMap = null;
            mat.roughnessMap = null;
            mat.normalMap = null;
            mat.metalness = (topPreset.metalness + barrelPreset.metalness) / 2;
            mat.roughness = (topPreset.roughness + barrelPreset.roughness) / 2;
            mat.needsUpdate = true;
          }
        }
      });
    } else if (finish) {
      const preset = FINISH_PRESETS[finish];
      if (!preset) return;
      clonedScene.traverse((child) => {
        if (child instanceof THREE.Mesh && child.material) {
          const mat = child.material as THREE.MeshStandardMaterial;
          if (mat.isMeshStandardMaterial) {
            mat.vertexColors = false;
            mat.map = null;
            mat.metalnessMap = null;
            mat.roughnessMap = null;
            mat.normalMap = null;
            mat.color.set(preset.color);
            mat.metalness = preset.metalness;
            mat.roughness = preset.roughness;
            mat.needsUpdate = true;
          }
        }
      });
    }

    if (standColor) {
      const standPreset = FINISH_PRESETS[standColor];
      if (standPreset) {
        clonedScene.traverse((child) => {
          if (child instanceof THREE.Mesh && child.geometry) {
            const box = new THREE.Box3().setFromObject(child);
            const modelBox = new THREE.Box3().setFromObject(clonedScene);
            const totalHeight = modelBox.max.y - modelBox.min.y;
            const meshTopRelative = (box.max.y - modelBox.min.y) / totalHeight;
            if (meshTopRelative < 0.4) {
              const mat = child.material as THREE.MeshStandardMaterial;
              if (mat.isMeshStandardMaterial) {
                mat.vertexColors = false;
                mat.map = null;
                mat.metalnessMap = null;
                mat.roughnessMap = null;
                mat.normalMap = null;
                mat.color.set(standPreset.color);
                mat.metalness = standPreset.metalness;
                mat.roughness = standPreset.roughness;
                mat.needsUpdate = true;
              }
            }
          }
        });
      }
    }
  }, [finish, dualFinish, standColor, clonedScene]);

  return (
    <group position={position}>
      <primitive object={clonedScene} rotation={rotation || [0, 0, 0]} />
    </group>
  );
}

const SceneOrbitControls = forwardRef(function SceneOrbitControls(
  {
    minPolarAngle,
    maxPolarAngle,
    target,
    minDistance,
    maxDistance,
  }: {
    minPolarAngle?: number;
    maxPolarAngle?: number;
    target?: [number, number, number];
    minDistance?: number;
    maxDistance?: number;
  },
  ref,
) {
  const { camera, gl } = useThree();
  const controls = useMemo(() => new OrbitControlsImpl(camera, gl.domElement), [camera, gl.domElement]);

  useImperativeHandle(ref, () => controls, [controls]);

  useEffect(() => {
    controls.enablePan = false;
    controls.minPolarAngle = minPolarAngle ?? Math.PI / 4;
    controls.maxPolarAngle = maxPolarAngle ?? Math.PI / 2.2;
    controls.minDistance = minDistance ?? 1.5;
    controls.maxDistance = maxDistance ?? 15;
    if (target) {
      controls.target.set(target[0], target[1], target[2]);
    }
    controls.update();
    return () => controls.dispose();
  }, [controls, minPolarAngle, maxPolarAngle, target, minDistance, maxDistance]);

  useFrame(() => controls.update());
  return null;
});

export default function SteelpanScene({ onDebug }: { onDebug?: (message: string) => void }) {
  const { envPreset, togglePreset } = useLightingPreset();
  const isDark = envPreset === "night";
  const bg = isDark ? "#000000" : "#111111";
  const controlsRef = useRef<any>(null);
  const navigate = useNavigate();
  const webglSupported = useMemo(() => supportsWebGL(), []);
  const [webglLost, setWebglLost] = useState(false);

  // Finish states
  const [bassTop, setBassTop] = useState("hi-gloss-black");
  const [bassBarrel, setBassBarrel] = useState("hi-gloss-black");
  const [tenorFinish, setTenorFinish] = useState("hi-chrome");
  const [celloFinish, setCelloFinish] = useState("hi-chrome");

  useEffect(() => {
    onDebug?.("SteelpanScene mounted");
  }, []);

  return (
    <div className="relative w-full h-screen" style={{ background: bg }}>
      {!webglSupported ? (
        <div className="flex min-h-screen items-center justify-center px-6 text-center" style={{ background: bg }}>
          <div className="max-w-md">
            <p className="text-sm tracking-widest uppercase text-white/50">Visualizer unavailable</p>
            <p className="mt-3 text-sm leading-relaxed text-white/60">
              This browser or device cannot create a WebGL context. Try a browser with hardware acceleration enabled, or use the text pages instead.
            </p>
          </div>
        </div>
      ) : (
      <Suspense fallback={<div className="flex min-h-screen items-center justify-center" style={{ background: bg }}><p className="text-white/40 text-sm tracking-widest uppercase animate-pulse">Initializing…</p></div>}>
        <WebGLFallbackBoundary fallback={<div className="flex min-h-screen items-center justify-center px-6 text-center" style={{ background: bg }}><div className="max-w-md"><p className="text-sm tracking-widest uppercase text-white/50">Visualizer unavailable</p><p className="mt-3 text-sm leading-relaxed text-white/60">The 3D scene could not start in this browser. Reload after enabling hardware acceleration or open the page on a device with WebGL support.</p></div></div>}>
        <Canvas
          camera={{ position: [0, 2.2, 5.2], fov: 38 }}
          dpr={[1, 1.5]}
          gl={{ antialias: true, alpha: false, powerPreference: "low-power" }}
          style={{ background: bg }}
          onCreated={({ gl }) => {
            const onLost = (event: Event) => {
              event.preventDefault();
              setWebglLost(true);
            };
            gl.domElement.addEventListener("webglcontextlost", onLost, false);
          }}
        >
          <color attach="background" args={[bg]} />
          <ambientLight intensity={isDark ? 0.8 : 0.8} />
          <spotLight position={[10, 10, 5]} angle={0.3} penumbra={1} intensity={isDark ? 4.0 : 3.0} castShadow />
          <spotLight position={[-10, 8, -5]} angle={0.3} penumbra={1} intensity={isDark ? 3.0 : 2.5} />
          <pointLight position={[0, -2, 5]} intensity={isDark ? 1.5 : 1.0} />
          <Environment preset={isDark ? "night" : "city"} background={false} environmentIntensity={isDark ? 0.6 : 1.0} />

          <Suspense
            fallback={
              <mesh>
                <boxGeometry args={[0.7, 0.7, 0.7]} />
                <meshStandardMaterial color="#777777" />
              </mesh>
            }
          >
            <Model url="models/bass.glb" position={[-0.9, 0.03, 0]} rotation={[0.15, -Math.PI / 2, 0]} dualFinish={{ top: bassTop, barrel: bassBarrel }} />
            <Model url="models/tenor.glb" position={[0, 0.03, 0]} rotation={[0, -Math.PI / 2, 0]} finish={tenorFinish} standColor="hi-gloss-black" />
            <Model url="models/cello.glb" position={[0.9, 0.03, 0]} rotation={[0.05, -Math.PI / 2, 0]} finish={celloFinish} standColor="hi-gloss-black" />
          </Suspense>

          <mesh rotation={[-Math.PI / 2, 0, 0]} position={[0, -0.48, 0]} receiveShadow>
            <planeGeometry args={[10, 10]} />
            <shadowMaterial opacity={0.2} />
          </mesh>
          <SceneOrbitControls
            ref={controlsRef}
            minPolarAngle={Math.PI / 4}
            maxPolarAngle={Math.PI / 2.2}
            target={[0, 0.4, 0]}
            minDistance={1.5}
            maxDistance={15}
          />
        </Canvas>
        </WebGLFallbackBoundary>
      </Suspense>
      )}

      {/* Finish togglers */}
      <div className="absolute top-24 left-6 z-10 flex flex-col gap-3">
        <div className="flex flex-col gap-2 p-3 rounded-xl bg-black/80 backdrop-blur-xl border border-white/10 w-52">
        <FinishPanel title="Bass" defaultOpen>
          <div className="flex flex-col gap-1.5">
            <div>
              <span className="text-[9px] tracking-[0.15em] uppercase text-white/40 mb-1 block">Top</span>
              <SwatchRow options={BASS_TOP_FINISHES} selected={bassTop} onSelect={setBassTop} />
            </div>
            <div>
              <span className="text-[9px] tracking-[0.15em] uppercase text-white/40 mb-1 block">Barrel</span>
              <SwatchRow options={BASS_BARREL_FINISHES} selected={bassBarrel} onSelect={setBassBarrel} />
            </div>
          </div>
        </FinishPanel>

        <div className="h-px bg-white/5" />

        <FinishPanel title="Tenor" defaultOpen>
          <SwatchRow options={TENOR_FINISHES} selected={tenorFinish} onSelect={setTenorFinish} />
        </FinishPanel>

        <div className="h-px bg-white/5" />

        <FinishPanel title="Cello" defaultOpen>
          <SwatchRow options={CELLO_FINISHES} selected={celloFinish} onSelect={setCelloFinish} />
        </FinishPanel>
        </div>
      </div>

      {/* Top nav — pan switcher */}
      <div className="absolute top-24 left-1/2 -translate-x-1/2 z-10">
        <div className="flex items-center gap-1 p-1.5 rounded-full bg-white/5 backdrop-blur-md border border-white/10">
          <button onClick={() => navigate({ to: "/visualizer/tenor" })} className="px-3 py-1.5 rounded-full text-[10px] tracking-[0.15em] uppercase text-white/40 hover:text-white/70 hover:bg-white/5 border border-transparent transition-all duration-200">Tenor</button>
          <button onClick={() => navigate({ to: "/visualizer/cello" })} className="px-3 py-1.5 rounded-full text-[10px] tracking-[0.15em] uppercase text-white/40 hover:text-white/70 hover:bg-white/5 border border-transparent transition-all duration-200">Cello</button>
          <button onClick={() => navigate({ to: "/visualizer/bass" })} className="px-3 py-1.5 rounded-full text-[10px] tracking-[0.15em] uppercase text-white/40 hover:text-white/70 hover:bg-white/5 border border-transparent transition-all duration-200">Bass</button>
        </div>
      </div>

      {/* Lighting toggle */}
      <div className="absolute top-24 right-6 z-10">
        <button
          onClick={togglePreset}
          title="Toggle lighting"
          className={`w-10 h-10 rounded-full flex items-center justify-center transition-all duration-200 backdrop-blur-md border ${
            isDark
              ? "bg-white/20 text-white border-white/30"
              : "bg-white/5 text-white/50 hover:text-white hover:bg-white/10 border-white/10"
          }`}
        >
          {isDark ? <Moon size={16} /> : <Sun size={16} />}
        </button>
      </div>
      {/* Camera controls */}
      <div className="absolute bottom-6 right-6 z-10">
        <CameraControlsButtons controlsRef={controlsRef} />
      </div>
      {webglLost && (
        <div className="absolute inset-0 flex items-center justify-center px-6 text-center">
          <div className="max-w-md">
            <p className="text-sm tracking-[0.35em] uppercase text-white/50">Visualizer unavailable</p>
            <p className="mt-3 text-sm leading-relaxed text-white/60">
              The WebGL context was lost while loading the scene. Reload after enabling hardware acceleration.
            </p>
          </div>
        </div>
      )}
    </div>
  );
}
